import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import { FileText, Upload, Trash2, Image, File } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingSkeleton } from '../../components/shared/LoadingSkeleton';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';

interface Document {
  id: number;
  file_name: string;
  document_type: string;
  file_size: number;
  created_at: string;
}

export default function DocumentsPage() {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => api.get<any>('/api/documents'),
  });

  const documents: Document[] = documentsData?.data || [];

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/documents/${id}`),
    onSuccess: () => {
      toast.success('Document deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete document');
    },
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', 'other');
      try {
        await api.upload('/api/documents/upload', formData);
        toast.success(`${file.name} uploaded successfully`);
      } catch (error: any) {
        toast.error(`Failed to upload ${file.name}: ${error.message}`);
      }
    }
    setUploading(false);
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  }, [queryClient]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'], 'application/pdf': ['.pdf'] },
    maxSize: 10 * 1024 * 1024,
  });

  const getIcon = (type: string) => {
    if (type.includes('image')) return <Image className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />;
    return <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-electric-500" />;
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        icon={FileText}
        iconColor="text-electric-400"
        title="Documents"
        subtitle="Manage uploaded files"
      />

      <div
        {...getRootProps()}
        className={`glass-card p-6 sm:p-8 rounded-2xl border-2 border-dashed transition-all cursor-pointer ${
          isDragActive
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10'
            : 'border-light-border dark:border-dark-border hover:border-purple-300'
        }`}
      >
        <input {...getInputProps()} />
        <div className="text-center">
          <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragActive ? 'text-purple-500' : 'text-light-text-muted'}`} />
          {uploading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto" />
          ) : (
            <>
              <p className="text-sm font-medium text-light-text dark:text-dark-text">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-xs text-light-text-muted mt-1">or click to browse (JPG, PNG, PDF — max 10MB)</p>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingSkeleton count={4} />
      ) : documents.length === 0 ? (
        <div className="text-center py-12">
          <File className="w-12 h-12 text-light-text-muted mx-auto mb-3" />
          <p className="text-sm text-light-text-muted">No documents uploaded yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
          {documents.map((doc: Document, index: number) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass-card p-3 sm:p-4 rounded-2xl"
            >
              <div className="flex items-start justify-between mb-3">
                {getIcon(doc.document_type)}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-light-text-muted hover:text-rose-500"
                  onClick={() => setDeleteConfirm(doc.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <h3 className="text-sm font-medium text-light-text dark:text-dark-text truncate">{doc.file_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-electric-100 dark:bg-electric-500/20 text-electric-700 dark:text-electric-300 uppercase">
                  {doc.document_type}
                </span>
                <span className="text-[10px] sm:text-xs text-light-text-muted">{formatSize(doc.file_size)}</span>
              </div>
              <p className="text-[10px] text-light-text-muted mt-2">
                {new Date(doc.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title="Delete Document"
        onConfirm={() => deleteMutation.mutate(deleteConfirm!)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
